/**
 * samlla#yy28: the single sanitizer for every "where to go after login" value.
 *
 * Every sink that accepts a post-login target from the user (the
 * `/auth/login?redirect=` guard, the Kinde `post_login_redirect_url` parameter,
 * the 401 handler in the API client) passes it through `sanitizeReturnTo()`.
 * Only a same-origin, relative path inside the allowlisted app areas survives;
 * anything else yields `null` and the caller falls back to the default target.
 */

/** Per-browser httpOnly cookie carrying the sanitized target through Kinde. */
export const RETURN_TO_COOKIE = 'samlla_return_to';
export const RETURN_TO_MAX_AGE_SECONDS = 600;
export const RETURN_TO_MAX_LENGTH = 2048;

const ALLOWED_PREFIXES = [
	'/app',
	'/pl/app',
	'/settings',
	'/pl/settings',
	'/oauth/consent',
	'/oauth/authorize/session'
];

// Sentinel origin: a same-origin relative path must keep it after parsing.
const SENTINEL_ORIGIN = 'https://return-to.invalid';

// eslint-disable-next-line no-control-regex
const UNSAFE_CHARS = /[\u0000-\u001f\u007f\\]/;

function decodedOnce(value: string): string | null {
	try {
		return decodeURIComponent(value);
	} catch {
		return null;
	}
}

function isAllowedPath(pathname: string): boolean {
	return ALLOWED_PREFIXES.some(
		(prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
	);
}

/**
 * Returns a normalized `pathname + search` when `raw` is a safe in-app target,
 * otherwise `null`. The fragment is dropped (it never reaches the server).
 */
export function sanitizeReturnTo(raw: unknown): string | null {
	if (typeof raw !== 'string') return null;
	if (raw.length === 0 || raw.length > RETURN_TO_MAX_LENGTH) return null;
	// Exactly one leading slash: rejects schemes, hosts and protocol-relative `//`.
	if (!raw.startsWith('/') || raw.startsWith('//')) return null;
	if (UNSAFE_CHARS.test(raw)) return null;

	// Percent-encoded tricks (`%5c`, `%2f%2f`, encoded control chars) are judged
	// on the once-decoded value too.
	const decoded = decodedOnce(raw);
	if (decoded === null) return null;
	if (decoded.startsWith('//') || UNSAFE_CHARS.test(decoded)) return null;

	let url: URL;
	try {
		url = new URL(raw, SENTINEL_ORIGIN);
	} catch {
		return null;
	}
	if (url.origin !== SENTINEL_ORIGIN) return null;
	if (url.username || url.password) return null;

	const decodedPath = decodedOnce(url.pathname);
	if (decodedPath === null || decodedPath.startsWith('//') || UNSAFE_CHARS.test(decodedPath)) {
		return null;
	}
	if (!isAllowedPath(url.pathname)) return null;

	const target = `${url.pathname}${url.search}`;
	return target.length > RETURN_TO_MAX_LENGTH ? null : target;
}

/** `?redirect=<target>` when `target` is a safe in-app path, otherwise `''`. */
export function returnToQuery(target: string | null): string {
	const safe = sanitizeReturnTo(target);
	return safe ? `?redirect=${encodeURIComponent(safe)}` : '';
}

/** `/auth/login`, carrying `target` as `?redirect=` when it is a safe in-app path. */
export function loginPathWithReturnTo(target: string | null): string {
	return `/auth/login${returnToQuery(target)}`;
}
